import { useRef, useState } from "react";
import { UploadCloud, X, Loader2, Map } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";

export function FirstTimeTip({ storageKey, title, body, onUpload, uploadLabel = "Upload a screenshot", onAdd, addLabel, testid = "first-tip" }) {
  const { resolved } = useTheme();
  const dark = resolved !== "light";
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(storageKey) === "1");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  if (dismissed) return null;

  const dismiss = () => { localStorage.setItem(storageKey, "1"); setDismissed(true); };
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try { await onUpload(f); } finally { setBusy(false); }
  };

  return (
    <div className="relative mb-4 flex items-start gap-3 rounded-2xl border border-[#9B2226]/45 bg-[#9B2226]/12 p-4" data-testid={testid}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#9B2226]/20 text-[#E0575B]"><Map className="h-5 w-5" /></div>
      <div className="min-w-0 pr-6">
        <p className={`font-display text-sm font-800 tracking-tight ${dark ? "text-zinc-50" : "text-zinc-900"}`}>{title}</p>
        <p className={`mt-0.5 text-xs ${dark ? "text-zinc-300" : "text-zinc-600"}`}>{body}</p>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`${testid}-input`} onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {onUpload && (
            <Button size="sm" onClick={() => !busy && fileRef.current?.click()} disabled={busy} className="h-8 bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid={`${testid}-upload`}>
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="mr-1.5 h-3.5 w-3.5" />}
              {busy ? "Reading…" : uploadLabel}
            </Button>
          )}
          {onAdd && addLabel && (
            <button onClick={onAdd} className={`text-xs font-600 ${dark ? "text-zinc-400 hover:text-zinc-200" : "text-zinc-500 hover:text-zinc-700"}`} data-testid={`${testid}-add`}>{addLabel}</button>
          )}
        </div>
      </div>
      <button onClick={dismiss} aria-label="Dismiss" data-testid={`${testid}-dismiss`} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-zinc-500/10 hover:text-zinc-300">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
